import {
  CURATION_MAX_ITEMS,
  CURATION_MIN_ITEMS,
  MARKET_SOURCE_CONFIG,
} from "./constants";
import {
  CurationItem,
  CoupangReference,
  DatasetSummary,
  DebateProposal,
  FinalConsensus,
  MarketSource,
  ProductCategory,
} from "./types";

export const toNum = (value: any, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const money = (value: number) =>
  `${Math.round(value).toLocaleString("ko-KR")}원`;

export const normalizeProductKey = (name: string) =>
  String(name || "")
    .toLowerCase()
    .replace(/\s+/g, "")
    .trim();

export const buildMarketSearchUrl = (source: MarketSource, keyword: string) =>
  `${MARKET_SOURCE_CONFIG[source].searchUrlPrefix}${encodeURIComponent(keyword)}`;

export const buildMarketProxyUrl = (source: MarketSource, keyword: string) =>
  `${MARKET_SOURCE_CONFIG[source].proxyPrefix}${encodeURIComponent(keyword)}`;

export const median = (nums: number[]): number | null => {
  if (!nums.length) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return Math.round((sorted[mid - 1] + sorted[mid]) / 2);
  }
  return sorted[mid];
};

export const extractCoupangPrices = (text: string): number[] => {
  const matches = Array.from(
    text.matchAll(/([1-9]\d{0,2}(?:,\d{3})+|[1-9]\d{2,6})\s*원/g),
  );
  const values = matches
    .map((m) => Number(String(m[1]).replace(/,/g, "")))
    .filter((n) => Number.isFinite(n) && n >= 300 && n <= 1_000_000);
  return Array.from(new Set(values)).slice(0, 12);
};

export const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const BEVERAGE_KEYWORDS = [
  "음료",
  "음료수",
  "커피",
  "아메리카노",
  "라떼",
  "티",
  "tea",
  "차",
  "주스",
  "쥬스",
  "에이드",
  "콜라",
  "사이다",
  "탄산",
  "소다",
  "밀크",
  "우유",
  "water",
  "cola",
  "ade",
  "smoothie",
];

const ZERO_BEVERAGE_KEYWORDS = ["제로", "zero", "0kcal", "0 kcal", "0칼로리"];
const LARGE_PACK_REGEX = /(\d{1,3})\s*(?:개입|입|봉|pack|팩)/i;
const MAX_BALANCED_QUANTITY_PER_ITEM = 24;
const CUSTOMER_REASON_HIDDEN_PATTERN = /(마진율|마진|수익성|이윤)/i;
const CUSTOMER_REASON_FALLBACK = "판매 데이터와 고객 선호를 반영한 추천";

type CurationReasonOptions = {
  customerFacing?: boolean;
};

export const inferCategory = (name: string): ProductCategory => {
  const lowered = (name || "").toLowerCase();
  return BEVERAGE_KEYWORDS.some((k) => lowered.includes(k.toLowerCase()))
    ? "beverage"
    : "snack";
};

export const parseBudgetFromRequirement = (
  requirement: string,
): number | null => {
  const text = String(requirement || "").replace(/,/g, "");
  if (!text.trim()) return null;

  const eokMatch = text.match(/(\d+(?:\.\d+)?)\s*억/);
  if (eokMatch) {
    return Math.round(Number(eokMatch[1]) * 100_000_000);
  }

  const manwonMatch = text.match(/(\d+(?:\.\d+)?)\s*만\s*원?/);
  if (manwonMatch) {
    return Math.round(Number(manwonMatch[1]) * 10_000);
  }

  const wonMatch = text.match(/(\d+(?:\.\d+)?)\s*원/);
  if (wonMatch) {
    return Math.round(Number(wonMatch[1]));
  }

  return null;
};

export const parseTargetItemCountFromRequirement = (
  requirement: string,
): number | null => {
  const text = String(requirement || "");
  const patterns = [
    /(?:품목|종류)\s*(?:은|는|을|를)?\s*(\d{1,3})\s*(?:종|개)/,
    /(\d{1,3})\s*(?:종|가지)\s*(?:품목|구성)?/,
    /(\d{1,3})\s*개\s*(?:품목|종류)/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) continue;
    const parsed = Number(match[1]);
    if (!Number.isFinite(parsed)) continue;
    return Math.max(CURATION_MIN_ITEMS, Math.min(CURATION_MAX_ITEMS, parsed));
  }
  return null;
};

type SnackBeverageRatio = {
  snack: number;
  beverage: number;
};

const parseSnackBeverageRatioFromRequirement = (
  requirement: string,
): SnackBeverageRatio | null => {
  const text = String(requirement || "");
  if (!/스낵|음료/.test(text)) return null;

  const ratioMatch = text.match(/(\d{1,2})\s*(?::|\/|대)\s*(\d{1,2})/);
  if (!ratioMatch) return null;

  const snack = Number(ratioMatch[1]);
  const beverage = Number(ratioMatch[2]);
  if (!Number.isFinite(snack) || !Number.isFinite(beverage)) return null;
  if (snack <= 0 || beverage <= 0) return null;

  return { snack, beverage };
};

type RequirementPolicy = {
  targetBudget: number | null;
  explicitItemCount: number | null;
  ratio: SnackBeverageRatio | null;
  excludeZeroBeverage: boolean;
  preferSmallSnackPack: boolean;
};

const parseRequirementPolicy = (requirement: string): RequirementPolicy => {
  const text = String(requirement || "");
  const lower = text.toLowerCase();

  const hasZero = /제로|zero|0\s*kcal|0칼로리/.test(lower);
  const hasExcludeIntent = /제외|금지|절대|말자|말아|빼|않|없/.test(text);
  const hasBeverageContext = /음료|탄산|콜라|사이다|소다/.test(text);

  const hasPackContext = /박스과자|개입|박스|묶음|대용량/.test(text);
  const hasSmallIntent = /적은\s*수|작은\s*수|소포장|낱개|대체|말고|보다는|줄여/.test(
    text,
  );

  return {
    targetBudget: parseBudgetFromRequirement(text),
    explicitItemCount: parseTargetItemCountFromRequirement(text),
    ratio: parseSnackBeverageRatioFromRequirement(text),
    excludeZeroBeverage: hasZero && hasExcludeIntent && hasBeverageContext,
    preferSmallSnackPack: hasPackContext && hasSmallIntent,
  };
};

export const computeTargetItemCount = (
  requirement: string,
  datasetSummary: DatasetSummary,
) => {
  const explicit = parseTargetItemCountFromRequirement(requirement);
  if (explicit != null) return explicit;

  const budget = parseBudgetFromRequirement(requirement);
  const diversityBoost = /다양|여러|폭넓|최대한|많이|넉넉/.test(requirement)
    ? 6
    : 0;

  let targetByBudget = CURATION_MIN_ITEMS;
  if (budget != null) {
    if (budget >= 3_000_000) targetByBudget = 32;
    else if (budget >= 2_000_000) targetByBudget = 28;
    else if (budget >= 1_000_000) targetByBudget = 22;
    else if (budget >= 500_000) targetByBudget = 16;
  } else {
    targetByBudget = Math.max(
      CURATION_MIN_ITEMS,
      Math.round(datasetSummary.top_products.length * 0.35),
    );
  }

  const upperBound = Math.max(
    CURATION_MIN_ITEMS,
    Math.min(
      CURATION_MAX_ITEMS,
      datasetSummary.top_products.length || CURATION_MAX_ITEMS,
    ),
  );

  return Math.max(
    CURATION_MIN_ITEMS,
    Math.min(upperBound, targetByBudget + diversityBoost),
  );
};

const stripJsonCodeFence = (text: string) => {
  const trimmed = String(text || "").trim();
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced?.[1]) {
    return fenced[1].trim();
  }
  return trimmed
    .replace(/^```json\s*/i, "")
    .replace(/^```\s*/i, "")
    .replace(/```$/i, "")
    .trim();
};

const normalizeControlCharsInJsonStringLiterals = (text: string) => {
  const source = String(text || "");
  let result = "";
  let inString = false;
  let escaped = false;

  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];

    if (inString) {
      if (escaped) {
        result += ch;
        escaped = false;
        continue;
      }
      if (ch === "\\") {
        result += ch;
        escaped = true;
        continue;
      }
      if (ch === '"') {
        result += ch;
        inString = false;
        continue;
      }
      if (ch === "\n" || ch === "\r" || ch === "\t") {
        result += " ";
        continue;
      }
      result += ch;
      continue;
    }

    if (ch === '"') {
      inString = true;
    }
    result += ch;
  }

  return result;
};

const getNextNonWhitespaceIndex = (text: string, start: number) => {
  for (let i = start; i < text.length; i += 1) {
    if (!/\s/.test(text[i])) return i;
  }
  return -1;
};

const isLikelyJsonValueStart = (text: string, start: number) => {
  const index = getNextNonWhitespaceIndex(text, start);
  if (index < 0) return false;

  const ch = text[index];
  if (
    ch === '"' ||
    ch === "{" ||
    ch === "[" ||
    ch === "-" ||
    (ch >= "0" && ch <= "9")
  ) {
    return true;
  }

  const rest = text.slice(index).toLowerCase();
  return (
    rest.startsWith("true") ||
    rest.startsWith("false") ||
    rest.startsWith("null")
  );
};

const isLikelyJsonStringTerminator = (text: string, quoteIndex: number) => {
  const nextIndex = getNextNonWhitespaceIndex(text, quoteIndex + 1);
  if (nextIndex < 0) return true;

  const nextChar = text[nextIndex];
  if (nextChar === ":" || nextChar === "}" || nextChar === "]") {
    return true;
  }
  if (nextChar !== ",") {
    return false;
  }

  const afterCommaIndex = getNextNonWhitespaceIndex(text, nextIndex + 1);
  if (afterCommaIndex < 0) return true;

  const afterCommaChar = text[afterCommaIndex];
  if (afterCommaChar === "}" || afterCommaChar === "]") {
    return true;
  }

  return isLikelyJsonValueStart(text, afterCommaIndex);
};

const escapeInnerQuotesInJsonStrings = (text: string) => {
  const source = String(text || "");
  let result = "";
  let inString = false;
  let escaped = false;

  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];

    if (inString) {
      if (escaped) {
        result += ch;
        escaped = false;
        continue;
      }
      if (ch === "\\") {
        result += ch;
        escaped = true;
        continue;
      }
      if (ch === '"') {
        if (isLikelyJsonStringTerminator(source, i)) {
          result += ch;
          inString = false;
        } else {
          result += '\\"';
        }
        continue;
      }
      result += ch;
      continue;
    }

    if (ch === '"') {
      inString = true;
    }
    result += ch;
  }

  return result;
};

const sanitizeJsonCandidate = (text: string) =>
  escapeInnerQuotesInJsonStrings(
    normalizeControlCharsInJsonStringLiterals(String(text || ""))
      .replace(/^\uFEFF/, "")
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'"),
  )
    .replace(/,\s*([}\]])/g, "$1")
    .trim();

const findBalancedJsonSegment = (text: string): string | null => {
  const source = String(text || "");
  let start = -1;
  const stack: string[] = [];
  let inString = false;
  let escaped = false;

  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];

    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === "\\") {
        escaped = true;
        continue;
      }
      if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      if (start >= 0) inString = true;
      continue;
    }

    if (start < 0) {
      if (ch === "{" || ch === "[") {
        start = i;
        stack.push(ch);
      }
      continue;
    }

    if (ch === "{" || ch === "[") {
      stack.push(ch);
      continue;
    }

    if (ch === "}" || ch === "]") {
      const last = stack[stack.length - 1];
      if (
        (ch === "}" && last === "{") ||
        (ch === "]" && last === "[")
      ) {
        stack.pop();
        if (stack.length === 0) {
          return source.slice(start, i + 1);
        }
      }
    }
  }

  return null;
};

const closeJsonDelimiters = (text: string) => {
  const source = String(text || "").trim();
  if (!source) return source;

  const stack: string[] = [];
  let inString = false;
  let escaped = false;

  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];

    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === "\\") {
        escaped = true;
        continue;
      }
      if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === "{" || ch === "[") {
      stack.push(ch);
      continue;
    }
    if (ch === "}" || ch === "]") {
      const last = stack[stack.length - 1];
      if (
        (ch === "}" && last === "{") ||
        (ch === "]" && last === "[")
      ) {
        stack.pop();
      }
    }
  }

  let repaired = source;
  if (inString) repaired += '"';
  while (stack.length > 0) {
    const opener = stack.pop();
    repaired += opener === "{" ? "}" : "]";
  }
  return repaired;
};

export const extractJson = (rawText: string): any => {
  const trimmed = String(rawText || "").trim();
  const deFenced = stripJsonCodeFence(trimmed);

  const candidates = Array.from(
    new Set(
      [
        trimmed,
        deFenced,
        findBalancedJsonSegment(trimmed),
        findBalancedJsonSegment(deFenced),
        closeJsonDelimiters(findBalancedJsonSegment(deFenced) || deFenced),
        closeJsonDelimiters(findBalancedJsonSegment(trimmed) || trimmed),
      ]
        .map((candidate) => sanitizeJsonCandidate(String(candidate || "")))
        .filter(Boolean),
    ),
  );

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch (_) {}
  }
  throw new Error("AI 응답을 JSON으로 파싱하지 못했습니다.");
};

export const normalizeCategory = (
  value: any,
  productName: string,
): ProductCategory => {
  const raw = String(value || "").toLowerCase();
  if (
    raw.includes("beverage") ||
    raw.includes("drink") ||
    raw.includes("음료")
  ) {
    return "beverage";
  }
  if (raw.includes("snack") || raw.includes("간식")) {
    return "snack";
  }
  return inferCategory(productName);
};

const withNormalizedItemNumbers = (item: CurationItem): CurationItem => {
  const quantity = Math.max(1, Math.round(toNum(item.quantity, 1)));
  const unitPrice = Math.max(1, Math.round(toNum(item.unit_price, 1)));
  return {
    ...item,
    quantity,
    unit_price: unitPrice,
    total_price: quantity * unitPrice,
    coupang_reference_price: unitPrice,
  };
};

const isZeroBeverageItem = (item: CurationItem) => {
  if (item.category !== "beverage") return false;
  const lowered = item.product_name.toLowerCase();
  return ZERO_BEVERAGE_KEYWORDS.some((k) => lowered.includes(k));
};

const getPackCount = (productName: string): number | null => {
  const match = String(productName || "").match(LARGE_PACK_REGEX);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
};

const isLargeSnackPackItem = (item: CurationItem) => {
  if (item.category !== "snack") return false;
  const packCount = getPackCount(item.product_name);
  return packCount != null && packCount >= 10;
};

const shouldExcludeByPolicy = (item: CurationItem, policy: RequirementPolicy) => {
  if (policy.excludeZeroBeverage && isZeroBeverageItem(item)) {
    return true;
  }
  if (policy.preferSmallSnackPack && isLargeSnackPackItem(item)) {
    return true;
  }
  return false;
};

export const normalizeItems = (rawItems: any[]): CurationItem[] => {
  if (!Array.isArray(rawItems)) return [];

  return rawItems
    .map((item) => {
      const productName = String(
        item?.product_name ?? item?.name ?? item?.productName ?? "",
      ).trim();
      const quantity = Math.max(0, Math.round(toNum(item?.quantity, 0)));
      const coupangReferencePrice = Math.max(
        0,
        Math.round(
          toNum(
            item?.coupang_reference_price,
            item?.unit_price ?? item?.reference_price ?? 0,
          ),
        ),
      );
      const unitPrice = coupangReferencePrice;
      const totalPrice = Math.max(
        0,
        Math.round(toNum(item?.total_price, unitPrice * quantity)),
      );
      const rawSearchUrl = String(
        item?.coupang_search_url ?? item?.reference_url ?? "",
      ).trim();
      const rawSource = String(
        item?.reference_source ?? item?.source ?? "coupang",
      ).toLowerCase();
      const inferredSourceFromUrl: MarketSource | null = rawSearchUrl.includes(
        "naver.com",
      )
        ? "naver_store"
        : rawSearchUrl.includes("kurly.com")
          ? "kurly"
          : rawSearchUrl.includes("11st.co.kr")
            ? "11st"
            : rawSearchUrl.includes("gmarket.co.kr")
              ? "gmarket"
              : rawSearchUrl.includes("coupang.com")
                ? "coupang"
                : null;
      const source: MarketSource =
        rawSource === "naver_store" ||
        rawSource === "naver" ||
        rawSource.includes("naver")
          ? "naver_store"
          : rawSource === "kurly" || rawSource.includes("컬리")
            ? "kurly"
            : rawSource === "11st" || rawSource.includes("11")
              ? "11st"
              : rawSource === "gmarket" ||
                  rawSource.includes("gmarket") ||
                  rawSource.includes("지마켓")
                ? "gmarket"
                : inferredSourceFromUrl || "coupang";
      const searchUrl =
        rawSearchUrl.includes("coupang.com") ||
        rawSearchUrl.includes("naver.com") ||
        rawSearchUrl.includes("kurly.com") ||
        rawSearchUrl.includes("gmarket.co.kr") ||
        rawSearchUrl.includes("11st.co.kr")
          ? rawSearchUrl
          : buildMarketSearchUrl(source, productName);
      const reason = String(item?.reason ?? "").trim();
      return {
        product_name: productName,
        category: normalizeCategory(item?.category, productName),
        quantity,
        unit_price: unitPrice,
        total_price: totalPrice,
        coupang_reference_price: coupangReferencePrice,
        coupang_search_url: searchUrl,
        reference_source: MARKET_SOURCE_CONFIG[source].label,
        reason,
      };
    })
    .filter(
      (item) =>
        item.product_name.length > 0 &&
        item.quantity > 0 &&
        item.unit_price > 0 &&
        item.total_price >= 0,
    );
};

export const computeRatioFromItems = (items: CurationItem[]) => {
  const snackCost = items
    .filter((x) => x.category === "snack")
    .reduce((acc, x) => acc + toNum(x.total_price), 0);
  const beverageCost = items
    .filter((x) => x.category === "beverage")
    .reduce((acc, x) => acc + toNum(x.total_price), 0);

  const total = snackCost + beverageCost;
  if (!total) return "-";

  const snackPart = Math.round((snackCost / total) * 10);
  const beveragePart = Math.max(0, 10 - snackPart);
  return `${snackPart}:${beveragePart}`;
};

export const findCoupangReference = (
  productName: string,
  references: CoupangReference[],
): CoupangReference | null => {
  if (!productName || references.length === 0) return null;

  const key = normalizeProductKey(productName);
  const direct = references.find(
    (ref) => normalizeProductKey(ref.product_name) === key,
  );
  if (direct) return direct;

  const partial = references.find((ref) => {
    const refKey = normalizeProductKey(ref.product_name);
    return key.includes(refKey) || refKey.includes(key);
  });

  return partial || null;
};

const findDatasetTopProduct = (
  productName: string,
  datasetSummary: DatasetSummary | undefined,
) => {
  if (!datasetSummary || !productName) return null;

  const key = normalizeProductKey(productName);
  const direct = datasetSummary.top_products.find(
    (product) => normalizeProductKey(product.product_name) === key,
  );
  if (direct) return direct;

  return (
    datasetSummary.top_products.find((product) => {
      const productKey = normalizeProductKey(product.product_name);
      return key.includes(productKey) || productKey.includes(key);
    }) || null
  );
};

const buildFallbackReason = (
  item: CurationItem,
  datasetSummary: DatasetSummary | undefined,
  references: CoupangReference[],
  options: CurationReasonOptions = {},
) => {
  const matchedProduct = findDatasetTopProduct(item.product_name, datasetSummary);
  const ref = findCoupangReference(item.product_name, references);
  const reasonParts: string[] = [];

  if ((matchedProduct?.total_quantity ?? 0) > 0) {
    reasonParts.push(`누적 판매 ${matchedProduct!.total_quantity}개`);
  }
  if ((matchedProduct?.outbound_quantity ?? 0) > 0) {
    reasonParts.push(`출고 ${matchedProduct!.outbound_quantity}개`);
  }
  if ((matchedProduct?.request_count ?? 0) > 0) {
    reasonParts.push(`간식신청 ${matchedProduct!.request_count}건`);
  }
  if (!options.customerFacing && (matchedProduct?.markup_percent ?? 0) > 0) {
    reasonParts.push(`마진 ${matchedProduct!.markup_percent}%`);
  }
  if (ref?.source_label) {
    reasonParts.push(`${ref.source_label} 가격 참조`);
  } else if (item.reference_source) {
    reasonParts.push(`${item.reference_source} 기준 선정`);
  }

  const fallback =
    reasonParts.slice(0, options.customerFacing ? 3 : 4).join(" + ") ||
    "판매데이터 기준으로 선정";
  return options.customerFacing
    ? sanitizeCustomerFacingReason(fallback)
    : fallback;
};

export const sanitizeCustomerFacingReason = (reason: string) => {
  const trimmed = String(reason || "").trim();
  if (!trimmed) return CUSTOMER_REASON_FALLBACK;

  const segments = trimmed
    .split(/\s*(?:\+\s*|\/\s*|,\s*|·\s*|;\s*|\|\s*|\n+)\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length > 1) {
    const visibleSegments = segments.filter(
      (segment) => !CUSTOMER_REASON_HIDDEN_PATTERN.test(segment),
    );
    const merged = visibleSegments.join(" + ").trim();
    return merged || CUSTOMER_REASON_FALLBACK;
  }

  const cleaned = trimmed
    .replace(
      /(?:또는|및|그리고|와)?\s*(?:높은\s*)?(?:마진율|마진|수익성|이윤)[^,+/·;]*/gi,
      " ",
    )
    .replace(/\s{2,}/g, " ")
    .replace(/^[,./+·;|\s]+|[,./+·;|\s]+$/g, "")
    .trim();

  return cleaned || CUSTOMER_REASON_FALLBACK;
};

export const ensureDiverseItems = (
  items: CurationItem[],
  datasetSummary: DatasetSummary | undefined,
  references: CoupangReference[],
  targetItemCount: number,
  maxItemCount: number,
  allowItem?: (item: CurationItem) => boolean,
  options: CurationReasonOptions = {},
): CurationItem[] => {
  const desiredCount = Math.max(
    CURATION_MIN_ITEMS,
    Math.min(targetItemCount, maxItemCount),
  );
  const merged = new Map<string, CurationItem>();

  items.forEach((item) => {
    const normalized = withNormalizedItemNumbers(item);
    if (allowItem && !allowItem(normalized)) return;
    const key = normalizeProductKey(normalized.product_name);
    if (!key || merged.has(key)) return;
    merged.set(key, normalized);
  });

  if (merged.size < desiredCount && datasetSummary) {
    const supplementProducts = [...datasetSummary.top_products].sort((a, b) => {
      if (a.curation_priority_score !== b.curation_priority_score) {
        return b.curation_priority_score - a.curation_priority_score;
      }
      if (a.remaining_stock_quantity !== b.remaining_stock_quantity) {
        return b.remaining_stock_quantity - a.remaining_stock_quantity;
      }
      return b.total_quantity - a.total_quantity;
    });

    for (const product of supplementProducts) {
      if (merged.size >= desiredCount) break;

      const canonicalKey = normalizeProductKey(product.product_name);
      if (!canonicalKey || merged.has(canonicalKey)) continue;

      const ref = findCoupangReference(product.product_name, references);
      const unitPrice = Math.max(
        1,
        Math.round(ref?.median_price || product.avg_unit_price || 1000),
      );
      const quantity = 1;
      const sourceLabel = ref?.source_label || "판매데이터";
      const reasonParts: string[] = [];
      if (product.total_quantity > 0) {
        reasonParts.push(`누적 판매 ${product.total_quantity}개`);
      }
      if (product.outbound_quantity > 0) {
        reasonParts.push(`출고 ${product.outbound_quantity}개`);
      }
      if (product.remaining_stock_quantity > 0) {
        reasonParts.push(`입출고 기준 잔여 ${product.remaining_stock_quantity}개`);
      }
      if (product.request_count > 0) {
        reasonParts.push(`간식신청 ${product.request_count}건`);
      }
      if (!options.customerFacing && (product.markup_percent ?? 0) > 0) {
        reasonParts.push(`마진 ${product.markup_percent}%`);
      }
      reasonParts.push(
        ref ? `${sourceLabel} 가격 참조로 다양성 보강` : "판매데이터 기준 다양성 보강",
      );
      const reason = options.customerFacing
        ? sanitizeCustomerFacingReason(reasonParts.join(" + "))
        : reasonParts.join(" + ");

      const candidate: CurationItem = {
        product_name: ref?.product_name || product.product_name,
        category: normalizeCategory(product.category_hint, product.product_name),
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
        coupang_reference_price: unitPrice,
        coupang_search_url:
          ref?.search_url || buildMarketSearchUrl("coupang", product.product_name),
        reference_source: sourceLabel,
        reason,
      };

      if (allowItem && !allowItem(candidate)) continue;
      merged.set(canonicalKey, candidate);
    }
  }

  return Array.from(merged.values()).slice(0, maxItemCount);
};

export const enforceCoupangOnlyItems = (
  items: CurationItem[],
  references: CoupangReference[],
  maxItems: number,
): CurationItem[] => {
  const merged = new Map<string, CurationItem>();

  items.forEach((item) => {
    const ref = findCoupangReference(item.product_name, references);
    const hasCoupangRef = Boolean(ref?.median_price);
    const canonicalName = hasCoupangRef ? ref!.product_name : item.product_name;
    const canonicalKey = normalizeProductKey(canonicalName);
    const quantity = Math.max(1, Math.round(item.quantity));
    const unitPrice = Math.max(
      1,
      Math.round(
        hasCoupangRef
          ? Number(ref!.median_price)
          : Number(item.coupang_reference_price || item.unit_price),
      ),
    );
    const reason = hasCoupangRef
      ? item.reason || `${ref!.source_label} 검색 결과 기반으로 선정`
      : item.reason || "외부 참조 없음, 판매데이터 기준으로 선정";
    const category = normalizeCategory(item.category, canonicalName);

    const existing = merged.get(canonicalKey);
    if (!existing) {
      merged.set(canonicalKey, {
        product_name: canonicalName,
        category,
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
        coupang_reference_price: unitPrice,
        coupang_search_url: hasCoupangRef
          ? ref!.search_url
          : buildMarketSearchUrl("coupang", canonicalName),
        reference_source: hasCoupangRef ? ref!.source_label : "판매데이터",
        reason,
      });
      return;
    }

    const nextQty = existing.quantity + quantity;
    merged.set(canonicalKey, {
      ...existing,
      quantity: nextQty,
      total_price: nextQty * existing.unit_price,
      reference_source: existing.reference_source,
      reason:
        existing.reason === reason || reason.length === 0
          ? existing.reason
          : `${existing.reason} / ${reason}`,
    });
  });

  return Array.from(merged.values()).slice(0, maxItems);
};

const sumCost = (items: CurationItem[]) =>
  items.reduce((acc, item) => acc + item.total_price, 0);

const sortByAffordability = (a: CurationItem, b: CurationItem) => {
  if (a.unit_price !== b.unit_price) return a.unit_price - b.unit_price;
  return a.total_price - b.total_price;
};

const sortByTargetUnitPrice = (
  targetUnitPrice: number,
  a: CurationItem,
  b: CurationItem,
) => {
  const aGap = Math.abs(a.unit_price - targetUnitPrice);
  const bGap = Math.abs(b.unit_price - targetUnitPrice);
  if (aGap !== bGap) return aGap - bGap;
  if (a.unit_price !== b.unit_price) return b.unit_price - a.unit_price;
  return a.total_price - b.total_price;
};

const pickItemsByCountAndRatio = (
  items: CurationItem[],
  targetCount: number,
  ratio: SnackBeverageRatio | null,
  preferAffordable = false,
  targetUnitPrice: number | null = null,
): CurationItem[] => {
  if (targetCount <= 0) return items;

  const sourceItems = [...items];
  if (targetUnitPrice != null && targetUnitPrice > 0) {
    sourceItems.sort((a, b) => sortByTargetUnitPrice(targetUnitPrice, a, b));
  } else if (preferAffordable) {
    sourceItems.sort(sortByAffordability);
  }
  if (!ratio) {
    return sourceItems.slice(0, targetCount);
  }

  const snackPool = sourceItems.filter((item) => item.category === "snack");
  const beveragePool = sourceItems.filter((item) => item.category === "beverage");
  const totalPart = ratio.snack + ratio.beverage;

  let snackNeed = Math.round((targetCount * ratio.snack) / totalPart);
  snackNeed = Math.max(0, Math.min(targetCount, snackNeed));
  let beverageNeed = targetCount - snackNeed;

  const selected: CurationItem[] = [];
  selected.push(...snackPool.slice(0, snackNeed));
  selected.push(...beveragePool.slice(0, beverageNeed));

  if (selected.length < targetCount) {
    const selectedKeys = new Set(selected.map((item) => normalizeProductKey(item.product_name)));
    const remains = sourceItems.filter(
      (item) => !selectedKeys.has(normalizeProductKey(item.product_name)),
    );
    selected.push(...remains.slice(0, targetCount - selected.length));
  }

  if (selected.length > targetCount) {
    return selected.slice(0, targetCount);
  }

  return selected;
};

const reduceCostToBudget = (
  items: CurationItem[],
  budget: number,
  minItemCount: number,
): CurationItem[] => {
  if (budget <= 0) return items;

  const next = items.map((item) => ({ ...item }));
  let currentTotal = sumCost(next);

  while (currentTotal > budget) {
    const reducible = next
      .filter((item) => item.quantity > 1)
      .sort((a, b) => b.unit_price - a.unit_price);
    if (reducible.length === 0) break;

    const target = reducible[0];
    const idx = next.findIndex(
      (item) => normalizeProductKey(item.product_name) === normalizeProductKey(target.product_name),
    );
    if (idx < 0) break;

    next[idx] = {
      ...next[idx],
      quantity: next[idx].quantity - 1,
      total_price: (next[idx].quantity - 1) * next[idx].unit_price,
    };
    currentTotal -= next[idx].unit_price;
  }

  while (currentTotal > budget && next.length > minItemCount) {
    const sorted = [...next].sort((a, b) => b.total_price - a.total_price);
    const removeKey = normalizeProductKey(sorted[0].product_name);
    const removeIdx = next.findIndex(
      (item) => normalizeProductKey(item.product_name) === removeKey,
    );
    if (removeIdx < 0) break;
    currentTotal -= next[removeIdx].total_price;
    next.splice(removeIdx, 1);
  }

  return next;
};

const growCostWithinBudget = (
  items: CurationItem[],
  budget: number,
  ratio: SnackBeverageRatio | null,
  maxQuantityPerItem: number,
): CurationItem[] => {
  if (budget <= 0 || items.length === 0) return items;

  const next = items.map((item) => ({ ...item }));
  let total = sumCost(next);
  const limit = budget * 0.97;
  if (total >= limit) return next;

  const ratioTotal = ratio ? ratio.snack + ratio.beverage : 0;
  let guard = 0;

  while (total < limit && guard < 6000) {
    guard += 1;

    let preferredCategory: ProductCategory | null = null;
    if (ratio && ratioTotal > 0) {
      const snackTarget = (budget * ratio.snack) / ratioTotal;
      const beverageTarget = budget - snackTarget;
      const snackCost = next
        .filter((item) => item.category === "snack")
        .reduce((acc, item) => acc + item.total_price, 0);
      const beverageCost = total - snackCost;
      const snackDeficit = snackTarget - snackCost;
      const beverageDeficit = beverageTarget - beverageCost;

      if (snackDeficit > 0 || beverageDeficit > 0) {
        preferredCategory =
          snackDeficit >= beverageDeficit ? "snack" : "beverage";
      }
    }

    const currentTotal = total;
    let candidates = next
      .filter((item) => {
        if (preferredCategory && item.category !== preferredCategory) return false;
        if (item.quantity >= maxQuantityPerItem) return false;
        if (currentTotal + item.unit_price > budget) return false;
        return true;
      })
      .sort((a, b) => {
        if (a.quantity !== b.quantity) return a.quantity - b.quantity;
        if (a.unit_price !== b.unit_price) return a.unit_price - b.unit_price;
        return normalizeProductKey(a.product_name).localeCompare(
          normalizeProductKey(b.product_name),
        );
      });

    if (candidates.length === 0 && preferredCategory) {
      candidates = next
        .filter((item) => {
          if (item.quantity >= maxQuantityPerItem) return false;
          if (currentTotal + item.unit_price > budget) return false;
          return true;
        })
        .sort((a, b) => {
          if (a.quantity !== b.quantity) return a.quantity - b.quantity;
          if (a.unit_price !== b.unit_price) return a.unit_price - b.unit_price;
          return normalizeProductKey(a.product_name).localeCompare(
            normalizeProductKey(b.product_name),
          );
        });
    }

    let updated = false;
    if (candidates.length > 0) {
      const candidate = candidates[0];
      const key = normalizeProductKey(candidate.product_name);
      const idx = next.findIndex(
        (item) => normalizeProductKey(item.product_name) === key,
      );
      if (idx >= 0) {
        next[idx] = {
          ...next[idx],
          quantity: next[idx].quantity + 1,
          total_price: (next[idx].quantity + 1) * next[idx].unit_price,
        };
        total += next[idx].unit_price;
        updated = true;
      }
    }

    if (!updated) break;
  }

  return next;
};

const applyRequirementPolicy = (
  items: CurationItem[],
  policy: RequirementPolicy,
  datasetSummary: DatasetSummary | undefined,
  references: CoupangReference[],
  targetItemCount: number,
  maxItemCount: number,
  options: CurationReasonOptions = {},
): CurationItem[] => {
  const allowItem = (item: CurationItem) => !shouldExcludeByPolicy(item, policy);

  let next = items.map(withNormalizedItemNumbers).filter(allowItem);

  const desiredCount = policy.explicitItemCount ?? targetItemCount;
  const desiredMax = policy.explicitItemCount ?? maxItemCount;

  if (next.length < desiredCount && datasetSummary) {
    next = ensureDiverseItems(
      next,
      datasetSummary,
      references,
      desiredCount,
      Math.max(desiredCount, desiredMax),
      allowItem,
      options,
    );
    next = next.map(withNormalizedItemNumbers).filter(allowItem);
  }

  if (policy.explicitItemCount != null) {
    const targetUnitPrice = policy.targetBudget
      ? Math.max(1, Math.round(policy.targetBudget / policy.explicitItemCount))
      : null;
    next = pickItemsByCountAndRatio(
      next,
      policy.explicitItemCount,
      policy.ratio,
      false,
      targetUnitPrice,
    );
  } else if (next.length > maxItemCount) {
    next = next.slice(0, maxItemCount);
  }

  if (policy.targetBudget != null && next.length > 0) {
    if (policy.explicitItemCount != null || next.length >= 24) {
      next = next.map((item) => ({
        ...item,
        quantity: 1,
        total_price: item.unit_price,
        coupang_reference_price: item.unit_price,
      }));
    }

    next = reduceCostToBudget(
      next,
      policy.targetBudget,
      policy.explicitItemCount ?? CURATION_MIN_ITEMS,
    );
    next = growCostWithinBudget(
      next,
      policy.targetBudget,
      policy.ratio,
      policy.explicitItemCount != null
        ? MAX_BALANCED_QUANTITY_PER_ITEM
        : MAX_BALANCED_QUANTITY_PER_ITEM * 2,
    );
    next = reduceCostToBudget(
      next,
      policy.targetBudget,
      policy.explicitItemCount ?? CURATION_MIN_ITEMS,
    );
  }

  return next.map(withNormalizedItemNumbers);
};

export const normalizeProposal = (
  raw: any,
  assistant: "chatgpt" | "gemini",
  coupangReferences: CoupangReference[],
  opts: {
    datasetSummary?: DatasetSummary;
    targetItemCount?: number;
    maxItemCount?: number;
    requirement?: string;
    customerFacing?: boolean;
  } = {},
): DebateProposal => {
  const policy = parseRequirementPolicy(opts.requirement || "");
  const maxItemCount = Math.max(
    CURATION_MIN_ITEMS,
    Math.min(CURATION_MAX_ITEMS, opts.maxItemCount ?? CURATION_MAX_ITEMS),
  );
  const targetItemCount = Math.max(
    CURATION_MIN_ITEMS,
    Math.min(maxItemCount, opts.targetItemCount ?? CURATION_MIN_ITEMS),
  );
  const rawObject =
    Array.isArray(raw) ? { items: raw } : raw && typeof raw === "object" ? raw : {};

  const normalizedItems = enforceCoupangOnlyItems(
    normalizeItems(rawObject?.items ?? []),
    coupangReferences,
    maxItemCount,
  );

  const diversifiedItems = ensureDiverseItems(
    normalizedItems,
    opts.datasetSummary,
    coupangReferences,
    targetItemCount,
    maxItemCount,
    undefined,
    { customerFacing: opts.customerFacing },
  );

  const items = applyRequirementPolicy(
    diversifiedItems,
    policy,
    opts.datasetSummary,
    coupangReferences,
    targetItemCount,
    maxItemCount,
    { customerFacing: opts.customerFacing },
  );
  const reasonCompletedItems = items.map((item) => ({
    ...item,
    reason:
      String(item.reason || "").trim() ||
      buildFallbackReason(item, opts.datasetSummary, coupangReferences, {
        customerFacing: opts.customerFacing,
      }),
  }));
  const normalizedReasonItems = opts.customerFacing
    ? reasonCompletedItems.map((item) => ({
        ...item,
        reason: sanitizeCustomerFacingReason(item.reason),
      }))
    : reasonCompletedItems;

  const estimatedFromItems = Math.round(sumCost(normalizedReasonItems));
  const targetBudget =
    policy.targetBudget ??
    (rawObject?.target_budget == null
      ? null
      : Math.round(toNum(rawObject?.target_budget)));

  const assumptions = Array.isArray(rawObject?.assumptions)
    ? rawObject.assumptions
        .map((x: any) => String(x).trim())
        .filter((x: string) => x.length > 0)
    : [];

  return {
    assistant,
    opinion: String(rawObject?.opinion ?? "").trim(),
    assumptions,
    target_budget: targetBudget,
    estimated_total_cost: estimatedFromItems,
    snack_beverage_ratio: computeRatioFromItems(normalizedReasonItems),
    items: normalizedReasonItems,
  };
};

export const normalizeFinalConsensus = (
  raw: any,
  coupangReferences: CoupangReference[],
  opts: {
    datasetSummary?: DatasetSummary;
    targetItemCount?: number;
    maxItemCount?: number;
    requirement?: string;
    customerFacing?: boolean;
  } = {},
): FinalConsensus => {
  const base = normalizeProposal(raw, "chatgpt", coupangReferences, opts);
  const rawObject =
    Array.isArray(raw) ? { items: raw } : raw && typeof raw === "object" ? raw : {};
  return {
    ...base,
    discussion_summary: String(rawObject?.discussion_summary ?? base.opinion).trim(),
  };
};
